import { save as safeSave } from '../../../lib/safeData';
/**
 * This container is the one in charge of validating the parameters received from the SSO client
 * in order to start the authentication process.
 */
class AuthContainer {
  /**
   * @param {ngLocation}        $location         To redirect the user once the parameters are
   *                                              validated.
   * @param {AuthRoutesManager} authRoutesManager To the get routes where the user will be
   *                                              redirected to.
   * @param {UIMessages}        uimessages        To show a notification in case a valid
   *                                              authentication reason is recevied.
   * @param {SSOClient}         ssoClient         To verify if the user is not already
   *                                              authenticated.
   * @param {SSOErrors}         ssoErrors         To generate errors in case the parameters are
   *                                              invalid.
   * @param {SSOState}          ssoState          To gernerate a state with the received
   *                                              parameters.
   * @param {SSOValidation}     ssoValidation     To validate the parameters.
   */
  constructor(
    $location,
    authRoutesManager,
    uimessages,
    ssoClient,
    ssoErrors,
    ssoState,
    ssoValidation,
  ) {
    'ngInject';

    /**
     * A local reference for the `$location` service.
     * @type {ngLocation}
     */
    this.$location = $location;
    /**
     * A local reference for the `authRoutesManager` service.
     * @type {AuthRoutesManager}
     */
    this.authRoutesManager = authRoutesManager;
    /**
     * A local reference for the `uimessages` service.
     * @type {UIMessages}
     */
    this.uimessages = uimessages;
    /**
     * A local reference for the `ssoClient` service.
     * @type {SSOClient}
     */
    this.ssoClient = ssoClient;
    /**
     * A local reference for the `ssoErrors` service.
     * @type {SSOErrors}
     */
    this.ssoErrors = ssoErrors;
    /**
     * A local reference for the `ssoState` service.
     * @type {SSOState}
     */
    this.ssoState = ssoState;
    /**
     * A local reference for the `ssoValidation` service.
     * @type {SSOValidation}
     */
    this.ssoValidation = ssoValidation;
  }
  /**
   * When the container gets mounted, read the parameters from the SSO Client (inserted on the
   * bindings), validate them and redirect the user to either the login screen or the error
   * page.
   */
  // eslint-disable-next-line complexity
  $onInit() {
    // Define what the state would look like if everything goes well.
    const state = {
      client: this.client,
      application: this.application,
      applicationPermissionsRequired: this.applicationPermissionsRequired,
      accountSelection: this.accountSelection,
      accountScope: this.accountScope,
      accountId: this.accountId ? Number(this.accountId) : 0,
      callback: this.callback,
      creatorToken: this.creatorToken,
    };

    if (state.callback && !this.ssoValidation.validateCallback(state.callback)) {
      /**
       * If the authentication method is `callback` and the URL is not from Photorank,
       * redirect to the error page.
       */
      this.ssoErrors.invalidCallback();
    } else if (!state.callback && !this.ssoValidation.validatePopup()) {
      /**
       * If the authentication method is `popup` and the `window` doesn't have an opener,
       * redirect to the error page.
       */
      this.ssoErrors.invalidPopup();
    } else {
      if (this.reason) {
        const reason = this.ssoValidation.validateReason(this.reason);
        if (reason) {
          this.uimessages.notification(
            reason,
            {
              type: 'info',
              expiration: 10000,
            },
          );
        }
      }

      // No error was found, so create the state.
      this.ssoState.createState(state);
      // Check if the user already has a cookie with a token.
      let token = this.ssoClient.getToken();

      if (!token) {
        // No cookie token
        // Use, if we have, the "SN Creator" generated token passed as parameter in the config
        token = state.creatorToken;
      }

      // Detect to which URL will the user be redirected to.
      let redirectTo;
      if (token) {
        // There was a token on the cookie, go to the success screen right away.
        redirectTo = this.authRoutesManager.success(safeSave(token));
      } else if (!this.client || this.client === 'admin') {
        // No token, so redirect the user to login screen.
        redirectTo = this.authRoutesManager.login(true);
      } else {
        // If client is not admin redirect the user to login screen.
        redirectTo = this.authRoutesManager.loginNORedirect(true);
      }

      this.$location.url(redirectTo);
    }
  }
}
/**
 * This container doesn't have a UI, it just validates the recevied parameters from the SSO
 * client and redirects the user to the appropriate page (error or login).
 * @ngesdoc component
 * @ngname authContainer
 * @ngcontroller {@link AuthContainer}
 * @ngcontainer
 * @ngmodule auth
 * @ngbinding {string}  client                         The name of the fontend app implementing
 *                                                     SSO.
 * @ngbinding {string}  application                    The ID of an AuthNZ application for which
 *                                                     the user is authenticating for.
 * @ngbinding {boolean} applicationPermissionsRequired Whether or not the user needs to have at
 *                                                     least one permission on the AuthNZ
 *                                                     application.
 * @ngbinding {boolean} accountSelection               Whether or not the user needs to select an
 *                                                     account.
 * @ngbinding {string}  accountScope                   The name of a permission account to filter
 *                                                     the accounts list.
 * @ngbinding {number}  accountId                      The ID of a preferred account the user wants
 *                                                     to log in with.
 * @ngbinding {?string} callback                       If the implementation type uses callback
 *                                                     redirections, this would be the URL where
 *                                                     the user needs to be redirected after being
 *                                                     authenticated.
 * @ngbinding {?string} reason                         The reason the authentication process was
 *                                                     started. This is the value of a constant
 *                                                     object on the SSO client and that the UI
 *                                                     can use to show a notification for cases
 *                                                     like when a session expired or the
 *                                                     implementation released a new version.
 *@ngbinding {?string} creatorToken                    An external token created in 'SN Creator' used in the frontend
 *                                                     app
 */
export default {
  controller: AuthContainer,
  bindings: {
    client: '@',
    application: '@',
    applicationPermissionsRequired: '<',
    accountSelection: '<',
    accountScope: '@',
    accountId: '@',
    callback: '@',
    reason: '@',
    creatorToken: '@',
  },
};
